/* import __COLOCATED_TEMPLATE__ from './sidebar.hbs'; */
/* RESPONSIBLE TEAM: team-workflows */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import { request } from 'embercom/lib/inbox/requests';
import type Session from 'embercom/services/session';

interface Args {
  conversationId: number;
}

interface TriggerDebuggingInfo {
  trigger_class: string;
  trigger_instances: {
    timestamp: Date;
    failure_reason: string;
    rulesets: {
      id: number;
      title: string;
      matched: boolean;
      executed: boolean;
      workflow_type: string;
      failure_reason: string;
    }[];
  }[];
}

interface Signature {
  Args: Args;
}

// mapping trigger_class to human friendly names
const triggerClassToName: Record<string, string> = {
  'Operator::Triggers::EndUserIdle::Fake':
    'workflows.sidebar.trigger-descriptions.customer-sends-a-message',
  'Operator::Triggers::ConversationStarted':
    'workflows.sidebar.trigger-descriptions.conversation-started',
  'Operator::Triggers::NewComment': 'workflows.sidebar.trigger-descriptions.teammate-replied',
  'Operator::Triggers::ConversationAssignmentChanged':
    'workflows.sidebar.trigger-descriptions.teammate-changes-conversation-team-assignment',
  'Operator::Triggers::ConversationStatusChanged':
    'workflows.sidebar.trigger-descriptions.conversation-state-changed',
  'Operator::Triggers::QuickReplySelected':
    'workflows.sidebar.trigger-descriptions.quick-reply-selected',
  'Operator::Triggers::Inactivity': 'workflows.sidebar.trigger-descriptions.inactivity',
  'Operator::Triggers::TicketCreated': 'workflows.sidebar.trigger-descriptions.ticket-created',
  'Operator::Triggers::TicketStateUpdated':
    'workflows.sidebar.trigger-descriptions.ticket-state-updated',
  'Operator::Triggers::ConversationAttributeChanged':
    'workflows.sidebar.trigger-descriptions.conversation-attribute-changed',
};

export default class WorkflowsSidebar extends Component<Signature> {
  @service declare session: Session;
  @service declare intl: IntlService;

  @tracked workflowDebuggingInfo: TriggerDebuggingInfo[] = [];
  @tracked selectedTrigger: $TSFixMe;
  @tracked fetchingWorkflows = false;
  @tracked fetchingTriggerData = false;
  @tracked selectedTriggerData: TriggerDebuggingInfo | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    this.fetchWorkflowDebuggingInfo();
  }

  get workflowTriggers() {
    return this.workflowDebuggingInfo.map((trigger: TriggerDebuggingInfo) => ({
      id: trigger.trigger_class,
      // map trigger_class to human friendly name if exists
      text: `${
        triggerClassToName[trigger.trigger_class]
          ? this.intl.t(triggerClassToName[trigger.trigger_class])
          : trigger.trigger_class
      } (${this.totalRulesetsCount(trigger.trigger_class)})`,
    }));
  }

  get triggerLevelFailureReason(): string | null {
    if (
      !this.selectedTriggerData ||
      !this.selectedTriggerData.trigger_instances ||
      this.selectedTriggerData.trigger_instances.length === 0
    ) {
      return null;
    }

    // Get the trigger-level failure reason from the first trigger instance
    // (All instances should have the same trigger-level failure reason)
    let failureReason = this.selectedTriggerData.trigger_instances[0]?.failure_reason;
    return failureReason && failureReason.trim() !== '' ? failureReason : null;
  }

  totalRulesetsCount(triggerId: string) {
    let trigger = this.workflowDebuggingInfo.find((trigger) => trigger.trigger_class === triggerId);

    if (!trigger) {
      return 0;
    }

    return trigger.trigger_instances.reduce((total, instance) => {
      return total + instance.rulesets.length;
    }, 0);
  }

  get triggerInstanceTables() {
    if (
      !this.selectedTriggerData ||
      !this.selectedTriggerData.trigger_instances ||
      this.selectedTriggerData.trigger_instances.length === 0
    ) {
      return [];
    }

    return this.selectedTriggerData.trigger_instances
      .map((instance) => ({
        timestamp: instance.timestamp,
        workflows: instance.rulesets
          .map((ruleset) => ({
            workflowId: ruleset.id,
            workflowName: ruleset.title,
            workflowType: ruleset.workflow_type,
            matched: ruleset.matched,
            executed: ruleset.executed,
            failureReason: ruleset.failure_reason,
          }))
          // Sort workflows: user_visible first, then background
          .sort((a, b) => {
            if (a.workflowType === 'user_visible' && b.workflowType === 'background') {
              return -1;
            }
            if (a.workflowType === 'background' && b.workflowType === 'user_visible') {
              return 1;
            }
            return 0;
          }),
      }))
      .filter((instanceTable) => instanceTable.workflows.length > 0);
  }

  @action
  onTriggerSelect(trigger: { id: string; text: string }) {
    this.fetchTriggerData(trigger.id);
    this.selectedTrigger = trigger;
  }

  private async fetchTriggerData(triggerId: string) {
    this.fetchingTriggerData = true;
    let url = `/ember/conversations/${this.args.conversationId}/debug_trigger?app_id=${this.session.workspace.id}&trigger_class=${triggerId}`;
    let response = await request(url);
    let trigger = await response.json();
    this.selectedTriggerData = trigger.trigger;
    this.fetchingTriggerData = false;
  }

  private async fetchWorkflowDebuggingInfo() {
    this.fetchingWorkflows = true;
    let response = await request(
      `/ember/conversations/${this.args.conversationId}/debug_workflows?app_id=${this.session.workspace.id}`,
    );

    this.workflowDebuggingInfo = (await response.json()).triggers;
    this.fetchingWorkflows = false;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Workflows::Sidebar': typeof WorkflowsSidebar;
    'inbox2/workflows/sidebar': typeof WorkflowsSidebar;
  }
}
